/**
 * Een lange voorvulling die het inloggen overleeft (R-37, R-154).
 *
 * **Het probleem, nagemeten.** Het doel van het inloggen reist in het flowcookie van de inlogdienst
 * (`saveFlow` in `apps/inlog/src/flow.ts`), en een voorvulling staat procentgecodeerd in de query van
 * dat doel. Bij een doel van 2.574 tekens is naam plus waarde van dat cookie 3.962 bytes, bij 2.774
 * tekens 4.229 bytes. Boven de 4.096 bytes gooit de browser het cookie weg, en dan eindigt het
 * terugkomen als een verlopen poging. Een paar honderd woorden voorgevulde tekst haalt die grens.
 *
 * **De oplossing blijft op de eigen oorsprong.** Een kort doel gaat zoals altijd met de query mee:
 * dat is de weg van R-37 en hij verandert niet. Is het doel te lang, dan gaat het zonder query naar
 * de inlogdienst, en staat de voorvulling zolang in `sessionStorage` van dit tabblad, per instantie en
 * met de levensduur van een inlogpoging (`FLOW_VALIDITY_S` in `apps/inlog/src/cookie.ts`). Na het
 * terugkomen leest de pagina haar één keer, wist haar, en geeft haar door zoals een voorvulling uit de
 * query (`prefillFromQuerystring` in `FormPage.tsx`): de pod vult daarmee alleen lege velden
 * (`applyPrefill` in `apps/runtime/src/prefill.ts`). De inlogdienst merkt er niets van.
 *
 * **Eenmalig, en dat is een eigenschap.** Een voorvulling die is gelezen, is weg; een herlaadslag van
 * het adres zonder query vult een veld dat de invuller met opzet leegmaakte dus niet opnieuw.
 *
 * Dit bestand is `window`-vrij: de opslag komt als argument binnen, zodat het in Node te toetsen is.
 */

/**
 * Tot welke lengte een doel zijn query houdt.
 *
 * Ruim onder de gemeten grens van ongeveer 2.650 tekens: de uitgever en de sleutel-id in het cookie
 * verschillen per omgeving, en een publicatie-adres op het cluster is langer dan op de loopback.
 */
export const PREFILL_TARGET_LIMIT = 2000

/** Hoe lang een bewaarde voorvulling geldt: zo lang als een inlogpoging (tien minuten). */
export const STASH_VALIDITY_MS = 10 * 60 * 1000

/** Wat er van de opslag nodig is; `window.sessionStorage` past erin. */
export interface PrefillStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

function keyFor(instantieId: string): string {
  return `ef-voorvulling:${instantieId}`
}

/**
 * Het doel voor de inlogdienst, met de voorvulling veilig onderweg.
 *
 * Kort genoeg: het adres met de query, zoals altijd. Te lang en aan een instantie te hangen: het adres
 * zonder query, met de voorvulling in de opslag. Lukt het bewaren niet (geblokkeerde sitegegevens, een
 * volle opslag), of is er geen instantie, dan het hele doel: dan loopt het mogelijk vast bij de
 * inlogdienst, maar gooit deze pagina niet zelf stil een voorvulling weg.
 */
export function targetKeepingPrefill(
  base: string,
  query: string,
  instantieId: string | undefined,
  storage: PrefillStorage,
  now: number,
): string {
  const full = `${base}${query}`
  if (query === '' || query === '?' || full.length <= PREFILL_TARGET_LIMIT || instantieId === undefined) return full
  const waarden = Object.fromEntries(new URLSearchParams(query).entries())
  try {
    storage.setItem(keyFor(instantieId), JSON.stringify({ tot: now + STASH_VALIDITY_MS, waarden }))
  } catch {
    return full
  }
  return base
}

/**
 * De bewaarde voorvulling van deze instantie, eenmalig: wat er staat wordt gewist, ook als het
 * verlopen of onleesbaar is. Nooit een fout; zonder iets bruikbaars een lege voorvulling.
 */
export function takeStashedPrefill(
  storage: PrefillStorage,
  instantieId: string | undefined,
  now: number,
): Readonly<Record<string, string>> {
  if (instantieId === undefined) return {}
  let saved: string | null
  try {
    saved = storage.getItem(keyFor(instantieId))
    if (saved !== null) storage.removeItem(keyFor(instantieId))
  } catch {
    return {}
  }
  if (saved === null) return {}
  try {
    const read = JSON.parse(saved) as { tot?: unknown; waarden?: unknown }
    if (typeof read.tot !== 'number' || read.tot < now) return {}
    if (typeof read.waarden !== 'object' || read.waarden === null) return {}
    const entries = Object.entries(read.waarden as Record<string, unknown>)
    if (!entries.every((entry): entry is [string, string] => typeof entry[1] === 'string')) return {}
    return Object.fromEntries(entries)
  } catch {
    return {}
  }
}
